import { useEffect, useState } from "react";
import { api, fileUrl } from "../lib/api";
import { Link } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { Users, Shield, Coins, Sparkles, Radio, AlertCircle, HelpCircle, Mail, UserPlus, Zap } from "lucide-react";

export default function NotFound() {
  const { user } = useAuth();
  const [d, setD] = useState(null);
  const [ai, setAi] = useState(null);
  const [tab, setTab] = useState("general");

  useEffect(() => {
    api.get("/dashboard").then(r => setD(r.data)).catch(()=>{});
    api.get("/dashboard/action-items").then(r => setAi(r.data)).catch(()=>{});
  }, []);
  if (!d) return <div className="p-8 mono text-[#A0A0A0]">// Loading help...</div>;

  const Stat = ({ icon: Icon, label, value, color = "amber" }) => (
    <div className="panel p-5">
      <div className="flex items-center gap-2 label-term mb-3"><Icon size={12} strokeWidth={1.5} /> {label}</div>
      <div className={`heading-mono text-3xl sm:text-4xl ${color}`}>{value}</div>
    </div>
  );
  return (
    <div className="p-4 sm:p-8">
      <div className="mb-6">
          <h1 className="heading-mono text-3xl sm:text-4xl amber amber-glow uppercase flicker">// Help</h1>
          <div className="mono text-xs text-[#A0A0A0] mt-1 tracking-widest uppercase">Field Manual</div>
      </div>

    <div className="flex gap-1 mb-4 border-b border-[#333] overflow-x-auto">
      {["general", "characters", "campaigns","store","comms","change-log"].map(t => (
        <button key={t} data-testid={`tab-${t}`} onClick={() => setTab(t)} className={`px-4 py-2 mono text-xs uppercase tracking-widest whitespace-nowrap ${tab===t?"amber border-b-2 border-[#FFB000]":"text-[#A0A0A0]"}`}>{t.replace("-"," ")}</button>
      ))}
    </div>


{tab === "general" && (
  <div className="panel p-5 mono text-sm text-[#A0A0A0] space-y-4">
    <h2 className="amber flicker text-lg tracking-widest">// TEXT FORMATTING (MARKDOWN)</h2>

    <p>
      Use simple markdown to format your notes, sessions, and comms logs.
      This helps keep things readable for your crew.
    </p>

    <div>
      <div className="amber">// BASIC FORMATTING</div>
      <ul className="list-disc pl-5 space-y-1">
        <li><b>Bold</b>: <code className="text-amber">**text**</code></li>
        <li><i>Italic</i>: <code className="text-amber">*text*</code></li>
        <li><code>Code</code>: <code className="text-amber">`text`</code></li>
      </ul>
    </div>

    <div>
      <div className="amber">// HEADINGS</div>
      <ul className="list-disc pl-5 space-y-1">
        <li><code className="text-amber"># Heading</code></li>
        <li><code className="text-amber">## Subheading</code></li>
        <li><code className="text-amber">### Section</code></li>
      </ul>
    </div>

    <div>
      <div className="amber">// LISTS</div>
      <ul className="list-disc pl-5 space-y-1">
        <li>Bullet list: <code className="text-amber">- item</code></li>
        <li>Numbered list: <code className="text-amber">1. item</code></li>
      </ul>
    </div>

    <div>
      <div className="amber">// LINKS</div>
      <p>
        <code className="text-amber">[label](https://spacebook.digital)</code>
      </p>
    </div>

    <div>
      <div className="amber">// TIPS</div>
      <ul className="list-disc pl-5 space-y-1">
        <li>Use headings to separate sessions or logs</li>
        <li>Use bold for important NPCs or items</li>
        <li>Use lists for inventory, crew members, or objectives</li>
      </ul>
    </div>

    <div className="text-xs text-[#666] pt-2">
      // Markdown rendering may vary slightly depending on the display module
    </div>
  </div>
)}



{tab === "characters" && (
  <div className="panel p-5 mono text-sm text-[#A0A0A0] space-y-4">
    <h2 className="amber text-lg tracking-widest">// CREW MANAGEMENT</h2>

    <p>
      Manage all active personnel in your campaigns. 
      Each crew member stores stats, equipment, and personal history.
    </p>

    <div>
      <div className="amber">// CREATE A NEW CHARACTER</div>
      <ol className="list-decimal pl-5 space-y-1">
        <li>Open <span className="text-amber">Characters</span> from navigation</li>
        <li>Select <code className="text-amber">+ NEW CHARACTER</code></li>
        <li>Enter a <span className="text-amber">Name</span></li>
        <li>Select a <span className="text-amber">Career</span></li>
        <li>Press <code className="text-amber">CREATE</code></li>
      </ol>
    </div>
    <div className="text-xs text-[#666] pt-2">
      // Characters can only be part of one campaign
    </div>

    <div>
      <div className="amber">// EDITING A CHARACTER</div>
      <ol className="list-decimal pl-5 space-y-1">
        <li>Select a crew member from the registry</li>
        <li>Update <span className="text-amber">Stats</span> and attributes with your GM</li>
        <li>Update health, stress, and status fields as required</li>
        <li>Press <code className="text-amber">SAVE</code> to apply changes</li>
        <li><span className="text-amber">XP</span> is rewarded after each session and can be used to upgrade your stats</li>
      </ol>
    </div>

    <div>
      <div className="amber">// CHARACTER SECTIONS</div>
      <ul className="list-disc pl-5 space-y-1">
        <li><span className="text-amber">Stats</span> — Core attributes and skills</li>
        <li><span className="text-amber">Inventory</span> — Equipment and carried items</li>
        <li><span className="text-amber">Backstory</span> — Character history and notes</li>
        <li><span className="text-amber">Campaign</span> — Active campaign links</li>
        <li><span className="text-amber">GM Messages</span> — Private updates from GM</li>
      </ul>
    </div>

    <div>
      <div className="amber">// BACKSTORY & NOTES</div>
      <p>
        Use the <span className="text-amber">Backstory</span> tab to record character history,
        personality, and mission notes.
      </p>
      <ul className="list-disc pl-5 space-y-1">
        <li>Coordinate details with your GM</li>
        <li>Define talents, appearance, and personal agenda</li>
        <li>Use markdown formatting for structured logs (See General tab for Markdown info)</li>
      </ul>
    </div>

    <div>
      <div className="amber">// TIPS</div>
      <ul className="list-disc pl-5 space-y-1">
        <li>Update stats after each session to stay in sync with your campaign</li>
        <li>Spend XP carefully — upgrades are permanent</li>
        <li>Keep backstory updated for better roleplay and continuity</li>
        <li>Once your character is created your GM can add it to the campaign</li>
      </ul>
    </div>

    <div className="text-xs text-[#666] pt-2">
      // Character data is persistent across sessions
    </div>
  </div>
)}

{tab === "campaigns" && (
  <div className="panel p-5 mono text-sm text-[#A0A0A0] space-y-4">
    <h2 className="amber text-lg tracking-widest">// CAMPAIGN OPERATIONS</h2>

    <p>
      A campaign is where the crew plays together. The person who creates it is the
      <span className="text-amber"> GM</span>; everyone else joins when the GM adds their character.
      You only see campaigns you are part of.
    </p>

    <div>
      <div className="amber">// CREATE A CAMPAIGN</div>
      <ol className="list-decimal pl-5 space-y-1">
        <li>Open <span className="text-amber">Campaigns</span> from navigation</li>
        <li>Select <code className="text-amber">+ NEW CAMPAIGN</code></li>
        <li>Enter a <span className="text-amber">Name</span> and pick a <span className="text-amber">Game Type</span> (Campaign or Cinematic)</li>
        <li>Add a <span className="text-amber">Description</span> (Markdown supported)</li>
        <li>Press <code className="text-amber">CREATE</code> — you are now its GM</li>
      </ol>
    </div>

    <div>
      <div className="amber">// JOINING THE PARTY</div>
      <ul className="list-disc pl-5 space-y-1">
        <li>Players: create your character first, then ask your GM to add it</li>
        <li>GM: open the <span className="text-amber">Party</span> tab and search under <span className="text-amber">Add Unassigned Characters</span></li>
        <li>Only characters not already in another campaign can be added</li>
        <li>The player is notified and the campaign appears in their list</li>
        <li>GM can <span className="text-amber">remove</span> a member — their character becomes unassigned again</li>
      </ul>
    </div>

    <div>
      <div className="amber">// RUNNING A SESSION</div>
      <ol className="list-decimal pl-5 space-y-1">
        <li>GM presses <code className="text-amber">START SESSION</code> — the party is notified</li>
        <li>Players press <code className="text-amber">CONFIRM: [character]</code> to mark attendance</li>
        <li>Play! The GM can open the store, run initiative, and log events</li>
        <li>GM presses <code className="text-amber">END SESSION</code> — the store closes and temporary effects wear off</li>
        <li>If attendance was taken, characters who did not confirm are logged as no-shows</li>
      </ol>
    </div>

    <div>
      <div className="amber">// XP QUESTIONNAIRE</div>
      <ul className="list-disc pl-5 space-y-1">
        <li>After a session ends, the questionnaire appears on the <span className="text-amber">Party</span> tab</li>
        <li>Answer each question — every <span className="text-amber">YES</span> is worth 1 XP</li>
        <li>Pick your character and press <code className="text-amber">SUBMIT &amp; COLLECT XP</code></li>
        <li>If attendance was taken, only confirmed characters can submit</li>
        <li>One submission per player per session</li>
        <li>GM can add campaign-specific questions under <span className="text-amber">Custom Questions</span></li>
      </ul>
    </div>

    <div>
      <div className="amber">// CAMPAIGN TABS</div>
      <ul className="list-disc pl-5 space-y-1">
        <li><span className="text-amber">Overview</span> — Description, party cash, store status, GM whispers</li>
        <li><span className="text-amber">Party</span> — Members, their characters, and the XP questionnaire</li>
        <li><span className="text-amber">NPCs</span> — Non-player characters; everyone sees them, only the GM opens their sheets</li>
        <li><span className="text-amber">Ship</span> — Ship stats, modules, damage, log, and shared inventory</li>
        <li><span className="text-amber">Initiative</span> — Combat order, HP, and range bands</li>
        <li><span className="text-amber">Threads</span> — GM-only plot notes (Open / Resolved / Failed)</li>
        <li><span className="text-amber">Timeline</span> — Sessions and key moments in order</li>
        <li><span className="text-amber">Sessions</span> — Session log, summaries, and history</li>
        <li><span className="text-amber">Chat</span> — Live campaign comms</li>
      </ul>
    </div>

    <div>
      <div className="amber">// SHIP INVENTORY</div>
      <ul className="list-disc pl-5 space-y-1">
        <li>Stow gear from a character's <span className="text-amber">Inventory</span> using the <code className="text-amber">STOW</code> button</li>
        <li>Any party member can take an item from the ship to any party character</li>
        <li>Good for trading gear or keeping a shared stash</li>
        <li>GM equips internal modules, upgrades, and armaments from the store</li>
      </ul>
    </div>

    <div>
      <div className="amber">// INITIATIVE TRACKER (GM)</div>
      <ul className="list-disc pl-5 space-y-1">
        <li>Can only be edited during an active session; players can watch</li>
        <li>Add party members, NPCs, or enemies from the <span className="text-amber">Enemy Library</span></li>
        <li>Drag rows to reorder, or use <code className="text-amber">SORT BY INIT &amp; SAVE</code></li>
        <li>HP <code className="text-amber">+</code>/<code className="text-amber">−</code> on a character also updates their sheet</li>
        <li>Move combatants between range bands: Engaged, Short, Medium, Long, Extreme</li>
      </ul>
    </div>

    <div>
      <div className="amber">// ENDING A CAMPAIGN</div>
      <ul className="list-disc pl-5 space-y-1">
        <li>GM presses <code className="text-amber">END CAMPAIGN</code> on the campaign page or card</li>
        <li>The campaign becomes <span className="text-amber">read-only</span> — history, chat, and logs are kept</li>
        <li>Ended campaigns move to the <span className="text-amber">Archive</span></li>
        <li>Only management can permanently delete a campaign</li>
      </ul>
    </div>

    <div>
      <div className="amber">// TIPS</div>
      <ul className="list-disc pl-5 space-y-1">
        <li>Write a session summary on the <span className="text-amber">Sessions</span> tab — it is posted to chat and kept in history</li>
        <li>Log deaths, injuries, and big purchases so they show on the timeline</li>
        <li>Use Threads to track mysteries and loose ends between sessions</li>
      </ul>
    </div>

    <div className="text-xs text-[#666] pt-2">
      // A character can only be in one campaign at a time
    </div>
  </div>
)}

{tab === "store" && (
  <div className="panel p-5 mono text-sm text-[#A0A0A0] space-y-4">
    <h2 className="amber text-lg tracking-widest">// COMPANY STORE</h2>

    <p>
      The Company Store sells equipment to your crew. Purchases are paid from
      <span className="text-amber"> Party Cash</span>, which is shared by everyone in the campaign.
    </p>

    <div>
      <div className="amber">// BUYING AN ITEM</div>
      <ol className="list-decimal pl-5 space-y-1">
        <li>Wait for your GM to open the store during a session</li>
        <li>Open <span className="text-amber">Store</span> from navigation</li>
        <li>Pick a category tab</li>
        <li>Choose which character the item is for</li>
        <li>Press <code className="text-amber">BUY</code> — the price comes out of party cash</li>
      </ol>
    </div>

    <div>
      <div className="amber">// WHEN CAN I BUY?</div>
      <ul className="list-disc pl-5 space-y-1">
        <li>Your character must be in a campaign</li>
        <li>A session must be active <i>and</i> the GM must have opened the store</li>
        <li>The party must have enough cash to cover the price</li>
        <li>The store closes automatically when the session ends</li>
      </ul>
    </div>

    <div>
      <div className="amber">// CATEGORIES</div>
      <ul className="list-disc pl-5 space-y-1">
        <li><span className="text-amber">Weapon</span> — Damage, modifier, ammo, and range</li>
        <li><span className="text-amber">Armor</span> — Rating and air supply</li>
        <li><span className="text-amber">Gear</span> — Equipment; some items can be used for effects</li>
        <li><span className="text-amber">Vehicle</span> — Bought for one character or for the whole party</li>
        <li><span className="text-amber">Internal Module / Armament / Upgrade</span> — Ship parts, equipped by the GM from the campaign's Ship tab</li>
      </ul>
    </div>

    <div>
      <div className="amber">// USING GEAR</div>
      <ul className="list-disc pl-5 space-y-1">
        <li>Some gear has effects: healing, stress, radiation, or a temporary stat boost</li>
        <li>Press <code className="text-amber">USE</code> on the item in your character's Inventory</li>
        <li><span className="text-amber">CONSUMABLE</span> items are removed after use</li>
        <li>Temporary stat boosts wear off when the session ends</li>
      </ul>
    </div>

    <div>
      <div className="amber">// GM: PARTY CASH &amp; THE STORE</div>
      <ul className="list-disc pl-5 space-y-1">
        <li>Add or remove party cash from the campaign <span className="text-amber">Overview</span> tab</li>
        <li><code className="text-amber">OPEN STORE</code> / <code className="text-amber">CLOSE STORE</code> is on the same tab and needs an active session</li>
        <li>When the GM presses <code className="text-amber">BUY</code>, the item is free — use it to hand out loot and drops</li>
        <li>GM buys work at any time, even with the store closed</li>
      </ul>
    </div>

    <div>
      <div className="amber">// SPECIAL ITEMS</div>
      <ul className="list-disc pl-5 space-y-1">
        <li>Marked <span className="text-amber">SPECIAL</span> and hidden from players</li>
        <li>Cannot be bought — only a GM can <code className="text-amber">GRANT</code> them</li>
        <li>Use for rare finds, story rewards, and mission-critical equipment</li>
      </ul>
    </div>

    <div className="text-xs text-[#666] pt-2">
      // Store stock is managed by management — ask them to add new items
    </div>
  </div>
)}

{tab === "comms" && (
  <div className="panel p-5 mono text-sm text-[#A0A0A0] space-y-4">
    <h2 className="amber text-lg tracking-widest">// COMMS</h2>

    <p>
      Keep in touch with your crew. Comms covers campaign chat, private messages
      between characters, and private channels with your GM.
    </p>

    <div>
      <div className="amber">// CAMPAIGN CHAT</div>
      <ul className="list-disc pl-5 space-y-1">
        <li>Open a campaign and select the <span className="text-amber">Chat</span> tab</li>
        <li>Type in the <code className="text-amber">&gt; transmit...</code> box and press Enter</li>
        <li>Messages show your character's name (or your username if you have no character there)</li>
        <li>Only the GM and party members can read it</li>
        <li><span className="text-amber">[SYSTEM]</span> messages announce sessions, purchases, XP, and more</li>
        <li>Chat becomes read-only when a campaign ends</li>
      </ul>
    </div>

    <div>
      <div className="amber">// MAKING FRIENDS</div>
      <ol className="list-decimal pl-5 space-y-1">
        <li>Open <span className="text-amber">Comms</span> from navigation</li>
        <li>Choose your character under <span className="text-amber">Acting as character</span></li>
        <li>Under <span className="text-amber">Find Characters</span>, search your crewmates</li>
        <li>Press <code className="text-amber">REQUEST</code> — the other player is notified</li>
        <li>They accept from <span className="text-amber">Pending Requests</span> (or their character page)</li>
      </ol>
    </div>
    <div className="text-xs text-[#666] pt-2">
      // Friendships are between characters, not players. Find Characters lists characters in the same campaign.
    </div>

    <div>
      <div className="amber">// PRIVATE MESSAGES</div>
      <ul className="list-disc pl-5 space-y-1">
        <li>Select a friend from your <span className="text-amber">Friends</span> list to open a channel</li>
        <li>Only the two characters' owners can read these messages</li>
        <li>The other player gets a notification for each new message</li>
        <li>Press <code className="text-amber">X</code> next to a friend to remove them</li>
      </ul>
    </div>

    <div>
      <div className="amber">// GM CHANNELS</div>
      <ul className="list-disc pl-5 space-y-1">
        <li>Every character in a campaign has a private channel with the GM</li>
        <li>Find them under <span className="text-amber">GM Channels</span> in Comms</li>
        <li>GM: send a quick whisper from <span className="text-amber">Private Whisper to Character</span> on the campaign Overview</li>
        <li>Only the character's owner and the GM can see the channel</li>
      </ul>
    </div>

    <div>
      <div className="amber">// NOTIFICATIONS</div>
      <ul className="list-disc pl-5 space-y-1">
        <li>The bell shows invites, friend requests, messages, sessions, and party cash changes</li>
        <li>Mark notifications as read or clear them individually or all at once</li>
        <li>Your <span className="text-amber">Dashboard</span> lists anything waiting on you</li>
      </ul>
    </div>

    <div>
      <div className="amber">// TIPS</div>
      <ul className="list-disc pl-5 space-y-1">
        <li>Use private messages to plan in character away from the rest of the party</li>
        <li>Players can reply in a GM Channel to ask the GM questions privately</li>
      </ul>
    </div>

    <div className="text-xs text-[#666] pt-2">
      // Messages are limited to 2000 characters
    </div>
  </div>
)}

{tab === "change-log" && (
  <div className="panel p-5 mono text-sm text-[#A0A0A0] space-y-4">
    <h2 className="amber text-lg tracking-widest">// CHANGE LOG</h2>

    <p>
      Changes to the terminal experience will be posted here.
    </p>

    <div>
      <div className="amber">// 1.76</div>
      <ol className="list-decimal pl-5 space-y-1">
        <li>Added Campaigns, Store and Comms pages to Help</li>
        <li>Dashboard refresh: characters show their campaign, health and stress, and are flagged when they have XP to spend; action items now include sessions waiting for your attendance and GM whispers; ended campaigns and NPCs no longer clutter the lists</li>
        <li>GM can now only add characters that are not already in another campaign</li>
        <li>Campaign chat and session details are now only visible to the GM and party members</li>
        <li>XP questionnaire can only be submitted once the session has ended, only counts the campaign's questions, and can no longer be submitted twice</li>
        <li>Removing a player from the party no longer removes their characters from other campaigns, and no longer fails if they have no character</li>
        <li>Party cash and personal cash can no longer go below zero when several purchases happen at once</li>
        <li>Ended campaigns are now fully read-only, including Threads, the session log and summary, Initiative HP, and vehicles</li>
        <li>Initiative Tracker no longer fails when an initiative or HP value is not a number</li>
        <li>Campaign pages, the Dashboard and Comms now load faster</li>
      </ol>
    </div>

    <div>
      <div className="amber">// 1.75</div>
      <ol className="list-decimal pl-5 space-y-1">
        <li>Special Items can now be added to the store which cannot be purchased or seen by characters, they can only be granted by a GM</li>
        <li>Renamed Armour Level to Rating and Modifier to Supply</li>
        <li>Ship upgrades now come from the store instead of manual input</li>
        <li>Added Notes field to Ship</li>
        <li>Removed Manufacturer from Ship and added Class</li>
        <li>Fixed issue which returned where GM could not grant items to players without party cash</li>
      </ol>
    </div>

    <div>
      <div className="amber">// 1.70</div>
      <ol className="list-decimal pl-5 space-y-1">
        <li>Enemy Stat Block Library — pre-built Xenomorph Drone/Warrior/Queen, Synthetics, etc. One click → adds to initiative with HP, attacks, signature moves. GM can also save custom enemies.</li>
        <li>Plot Threads Tracker — sticky notes for ongoing mysteries / NPCs / objectives, only GM sees.</li>
        <li>Campaign Timeline View — sessions on a vertical timeline with key moments highlighted (deaths, level-ups, big purchases).</li>
        <li>Range Band Tracker in Initiative — Alien combat is range-band-driven (Engaged/Short/Medium/Long/Extreme). Visual lanes in the initiative tracker.</li>
      </ol>
    </div>

    <div>
      <div className="amber">// 1.64</div>
      <ol className="list-decimal pl-5 space-y-1">
        <li>NPC tab was not visible to anyone but a GM, now readable by all characters</li>
        <li>Notifications can now be marked as read and cleared either inidivually or all at once</li>
        <li>Backstory in Character page now defaults to preview instead of edit mode</li>
        <li>NPC characters no longer appear twice in GM Whispers and Session Notes drop downs</li>
      </ol>
    </div>

    <div>
      <div className="amber">// 1.63</div>
      <ol className="list-decimal pl-5 space-y-1">
        <li>Campaign chat now shows the Character Name instead of username, unless it's a GM who does not have a character</li>
        <li>Spending XP now works correctly and follows proper protocols</li>
        <li>GM can now send private messages to characters</li>
        <li>Improvements made to Initiative Tracker</li>
        <li>Chat now scrolls to the bottom on load for a nicer UX</li>
      </ol>
    </div>

    <div>
      <div className="amber">// 1.62</div>
      <ol className="list-decimal pl-5 space-y-1">
        <li>GM can now set the Career of an NPC</li>
        <li>Fixed an issue where the GM could not add inventory to characters without spending cash</li>
        <li>Moved disconnect button further up the side menu as it was hard to reach on a PDA</li>
        <li>Users can now change their passwords, Management can also reset forgotten passwords</li>
        <li>Characters now need to confirm attendance when a session starts</li>
        <li>Improved UX when adding a character to the party</li>
        <li>Inventory can now be stowed on the ship and another character can take them (Good for trading)</li>
        <li>Initiative Tracker added, this feature is in beta</li>
      </ol>
    </div>

  </div>
)}


    </div>
  );
}
